import { useVerificationDisclosureOpen, ModalTitle } from '@overlayqa/shared/ui';
import { BubbleChatIcon } from '@hugeicons/core-free-icons';
/**
 * FeedbackFab — the dashboard's bottom-right floating speech-bubble button.
 *
 * The bubble icon promised a conversation; it now delivers one: clicking opens
 * the AI help agent (ChatThread) instead of the old one-shot feedback form
 * (which had 7+ clickers and 0 completed submissions in 90 days). The shell
 * owns the FAB button, open/close state, outside-click and Escape handling;
 * the conversation lives in ChatThread and resets per open (v1 ephemeral).
 *
 * It also renders the post-onboarding "Any questions?" nudge — a 1:1 port of
 * the extension's HelpFab Intercom card (packages/extension/src/content/help/
 * HelpFab.tsx). It previews the chat's first message above the FAB and recurs
 * on each dashboard load until the user dismisses it with the x, then never
 * again (lib/helpNudge one-shot). Opening the chat only hides it while the
 * chat is open (Emily, 2026-10-26). The Layout only mounts this
 * for onboarding-completed users (Layout.tsx redirects the rest), so no
 * mid-onboarding/auth-view guards are needed here.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSonner } from 'sonner';
import { capture } from '../lib/analytics';
import { ChatThread } from './ChatThread';
import { LogoMark } from './ui/Logo';
import { HELP_GREETING } from './help/greeting';
import { hasSeenHelpNudge, markHelpNudgeSeen } from '../lib/helpNudge';
import { useDialogFocus } from '@overlayqa/shared/ui';
import { useIsDesktopLayout } from '../lib/desktopLayout';
import { log } from '@/lib/logger';

// Module-scope latch so the help_nudge_shown impression fires at most once per
// page session, even across FeedbackFab remounts (matches the extension's
// nudgeShownEmitted latch — the sibling celebration impression double-fired on
// a shell remount in prod before its emission was latched). Reset in tests.
let nudgeShownEmitted = false;

/** Test-only: reset the module-scope nudge impression latch. */
export function resetHelpNudgeStateForTests(): void {
  nudgeShownEmitted = false;
}

// Pages whose content reaches the bottom-right corner at phone width, where the
// card covered it (the Danger zone text and Branding on Account settings,
// 2026-11-01 phone frames). Emily, 2026-11-01: hide the card there on phones.
const PHONE_CARD_HIDDEN_PATHS = new Set(['/settings']);

interface FeedbackFabProps {
  /** The current route, from Layout (this component renders outside a Router in its tests). */
  pathname?: string;
}

export function FeedbackFab({ pathname = '' }: FeedbackFabProps = {}) {
  const [isOpen, setIsOpen] = useState(false);
  // Read the persisted one-shot synchronously at mount — localStorage is
  // authoritative and race-free (no fast-init store default to guard against).
  const [nudgeDismissed, setNudgeDismissed] = useState(() => hasSeenHelpNudge());
  const popoverRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const verificationOpen = useVerificationDisclosureOpen(fabRef);
  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const showNudge = !nudgeDismissed && !isOpen && !verificationOpen;
  // The card and the Toaster share the bottom-right corner (Layout's Toaster
  // offset 88px, the card bottom-[76px]), so a toast used to land on the card
  // with its text showing through. Emily, 2026-10-21, option (a): the card
  // steps aside while any toast is up and returns when the last one leaves.
  // Only the card: the FAB badge stays, and a toast never retires the nudge.
  // useSonner drops a toast when the Toaster publishes its dismiss, which it
  // does on timeout too (sonner 2.0.7 removeToast -> ToastState.dismiss).
  // The extension needs none of this: its toasts sit centred on the page.
  const toastShowing = useSonner().toasts.length > 0;
  // The launcher and its badge stay; only the card yields, like the toast case.
  const coversPhoneContent = !useIsDesktopLayout() && PHONE_CARD_HIDDEN_PATHS.has(pathname);
  const cardVisible = showNudge && !toastShowing && !coversPhoneContent;

  // Trap Tab inside the popover and hand focus back to the FAB on close.
  useDialogFocus(isOpen, popoverRef, fabRef);

  // Impression fires only when the nudge actually renders, latched once per
  // page session. Persist happens on the x, not here.
  useEffect(() => {
    if (cardVisible && !nudgeShownEmitted) {
      nudgeShownEmitted = true;
      capture('help_nudge_shown');
    }
  }, [cardVisible]);

  // The x is the only dismissal: hide AND persist the one-shot so it stops
  // recurring on future loads. Parity with the extension's HelpFab.
  const dismissNudge = useCallback(() => {
    capture('help_nudge_dismissed');
    setNudgeDismissed(true);
    markHelpNudgeSeen();
  }, []);

  const openChat = useCallback(() => {
    log.info('[FeedbackFab] opening help chat');
    capture('help_agent_opened');
    setIsOpen(true);
  }, []);

  // Close when clicking outside the FAB or popover.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(target) &&
        fabRef.current && !fabRef.current.contains(target)
      ) {
        log.info('[FeedbackFab] click outside, closing help chat');
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        log.info('[FeedbackFab] escape pressed, closing help chat');
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    if (isOpenRef.current) {
      log.info('[FeedbackFab] FAB clicked, closing');
      setIsOpen(false);
      return;
    }
    openChat();
  }, [openChat]);

  return (
    <>
      <button
        ref={fabRef}
        style={verificationOpen && !isOpen ? { display: 'none' } : undefined}
        type="button"
        onClick={handleToggle}
        aria-label={isOpen ? 'Close help chat' : 'Open help chat'}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full text-white shadow-[shadow:var(--oq-elevation-popover)] transition hover:scale-105 ${
          isOpen ? 'bg-primary-600' : 'bg-primary-500 hover:bg-primary-600'
        }`}
      >
        {isOpen ? (
          <svg width="19" height="19" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M4.5 4.5L13.5 13.5M4.5 13.5L13.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M4 14l-2 3v-13a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {showNudge && (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-1 flex h-[22px] min-w-[22px] items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 oq-text-micro text-white shadow-[shadow:var(--oq-elevation-sm)]"
          >
            1
          </span>
        )}
      </button>

      {cardVisible && (
        <div role="status" className="oq-help-nudge-anim fixed bottom-[76px] right-6 z-40 w-[300px] max-w-[calc(100vw-var(--oq-space-12))]">
          {/* stacked-card "peek" strip behind the top edge */}
          <span
            aria-hidden="true"
            className="absolute left-3 right-3 -top-2 z-[1] h-7 rounded-t-xl rounded-b-lg border border-gray-200 bg-white shadow-[shadow:var(--oq-elevation-sm)]"
          />
          <button
            type="button"
            onClick={openChat}
            aria-label="Open help chat about your issues"
            className="relative z-[2] flex w-full items-start gap-3 rounded-xl border border-gray-200 bg-white px-3 pb-4 pt-3 pr-8 text-left shadow-[shadow:var(--oq-elevation-hover)] transition hover:-translate-y-px hover:shadow-[shadow:var(--oq-elevation-popover)]"
          >
            <span
              aria-hidden="true"
              className="oq-help-nudge-avatar grid h-7 w-7 flex-none place-items-center rounded-full text-white shadow-[shadow:var(--oq-elevation-sm)]"
            >
              <LogoMark className="h-[15px] w-[15px] text-white" />
            </span>
            <span className="line-clamp-2 oq-text-body text-gray-700">
              {HELP_GREETING}
            </span>
          </button>
          <button
            type="button"
            onClick={dismissNudge}
            aria-label="Dismiss"
            className="absolute right-2 top-2 z-[3] grid h-[22px] w-[22px] place-items-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="pointer-events-none" width="12" height="12" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M4.5 4.5L13.5 13.5M4.5 13.5L13.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {isOpen && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-modal="true"
          aria-label="OverlayQA Help"
          className="fixed bottom-[76px] right-6 z-40 w-[340px] max-w-[calc(100vw-var(--oq-space-12))] max-h-[calc(100dvh-var(--oq-space-12)*2)] flex flex-col overflow-hidden rounded-xl bg-white p-4 shadow-xl"
        >
          <div className="mb-3 flex shrink-0 items-center justify-between border-b border-gray-100 pb-3">
            <ModalTitle icon={BubbleChatIcon} className="oq-text-heading text-gray-900">OverlayQA Help</ModalTitle>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
              className="text-gray-400 transition-colors hover:text-gray-600"
            >
              <svg width="14" height="14" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M4.5 4.5L13.5 13.5M4.5 13.5L13.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <ChatThread />
        </div>
      )}
    </>
  );
}
